import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBookingsByUser, cancelBooking } from '../../services/bookingService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { formatPaise } from '../../utils/money';
import NotificationToggle from '../../components/NotificationToggle';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';

const MyBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancelReason, setCancelReason] = useState('');
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const { token, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token || !user?.id) {
            navigate('/login');
            return;
        }
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            const data = await getBookingsByUser(user.id);
            setBookings(Array.isArray(data) ? data : []);
        } catch (error) {
            toast.error('Error fetching bookings');
        } finally {
            setLoading(false);
        }
    };
    useLiveRefresh(fetchBookings);

    // Modal open karo
    const openCancelModal = (bookingId) => {
        setSelectedBooking(bookingId);
        setCancelReason('');
        setShowModal(true);
    };

    // Modal band karo
    const closeCancelModal = () => {
        setShowModal(false);
        setSelectedBooking(null);
        setCancelReason('');
    };

    const handleCancel = async () => {
        if (!cancelReason.trim()) {
            toast.warning('Please enter cancellation reason');
            return;
        }
        setCancelling(true);
        try {
            await cancelBooking(selectedBooking, cancelReason);
            toast.success('Booking cancelled successfully');
            closeCancelModal();
            fetchBookings();
        } catch (error) {
            toast.error(error.response?.data?.message
                || 'Error cancelling booking');
        } finally {
            setCancelling(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            PENDING: 'warning',
            CONFIRMED: 'primary',
            COMPLETED: 'success',
            CANCELLED: 'danger',
            REJECTED: 'dark'
        };
        return badges[status] || 'secondary';
    };

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border"
                style={{ color: '#B22222' }}>
            </div>
            <p className="mt-2 text-muted">Loading bookings...</p>
        </div>
    );

    return (
        <div className="container py-4">
             <NotificationToggle role="USER" />
            <h2 className="fw-bold mb-4"
                style={{ color: '#8B0000' }}>
                📅 My Bookings
            </h2>

            {bookings.length === 0 ? (
                <div className="text-center py-5">
                    <h4 style={{ color: '#8B0000' }}>
                        No bookings found
                    </h4>
                    <p className="text-muted">
                        You haven't made any bookings yet
                    </p>
                    <button
                        className="btn fw-bold"
                        style={{
                            backgroundColor: '#B22222',
                            color: '#FFD700',
                            border: 'none'
                        }}
                        onClick={() => navigate('/search')}>
                        Find a Poojari
                    </button>
                </div>
            ) : (
                <div className="row g-3">
                    {bookings.map(booking => (
                        <div key={booking.id} className="col-12">
                            <div className="card shadow-sm"
                                style={{
                                    borderRadius: '16px',
                                    border: '1px solid #FFD700'
                                }}>
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        <div className="col-md-8">
                                            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                                <h5 className="mb-0"
                                                    style={{ color: '#8B0000' }}>
                                                    Booking #{booking.id}
                                                </h5>
                                                <span className={`badge bg-${getStatusBadge(booking.bookingStatus)}`}>
                                                    {booking.bookingStatus}
                                                </span>
                                                <span className={`badge ${booking.paymentStatus === 'COMPLETED'
                                                    ? 'bg-success'
                                                    : 'bg-warning text-dark'}`}>
                                                    Payment: {booking.paymentStatus}
                                                </span>
                                            </div>
                                            <p className="mb-1">
                                                🙏 Pooja: <strong>{booking.poojaType}</strong>
                                            </p>
                                            <p className="mb-1">
                                                📅 Date: <strong>{booking.poojaDate}</strong> at <strong>{booking.poojaTime}</strong>
                                            </p>
                                            <p className="mb-1">
                                                📍 {booking.poojaAddress}, {booking.poojaCity}
                                            </p>
                                            {booking.cancellationReason && (
                                                <p className="mb-1 text-danger">
                                                    ❌ Reason: {booking.cancellationReason}
                                                </p>
                                            )}
                                            <p className="mb-0 fw-bold"
                                                style={{ color: '#B22222' }}>
                                                {formatPaise(booking.bookingAmount)}
                                            </p>
                                            
                                            {/* ⚠️ SAMPARK - sirf pooja se 24 ghante pehle.
                                                Backend tay karta hai ki dikhe ya nahi -
                                                contactPhone tabhi aata hai. Yahan dobara
                                                shart nahi lagayi, warna do jagah do
                                                niyam ho jaate. */}
                                            {booking.contactPhone && (
                                                <>
                                                    <a href={`tel:${booking.contactPhone}`}
                                                        className="btn btn-sm fw-bold mt-2"
                                                        style={{
                                                            backgroundColor: '#2E7D32',
                                                            color: 'white',
                                                            borderRadius: '8px',
                                                            fontSize: '13px'
                                                        }}>
                                                        📞 Call {booking.contactName || 'poojari'}
                                                    </a>
                                                    <div className="text-muted mt-1"
                                                        style={{ fontSize: '11px' }}>
                                                        {booking.contactPhone}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className="col-md-4 text-md-end mt-3 mt-md-0">
                                            {booking.bookingStatus === 'PENDING' && (
                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm fw-bold"
                                                    onClick={() =>
                                                        openCancelModal(booking.id)}>
                                                    Cancel Booking
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ===== React Custom Modal ===== */}
            {showModal && (
                <>
                    {/* Backdrop */}
                    <div
                        onClick={closeCancelModal}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1050
                        }}>
                    </div>

                    {/* Modal Box */}
                    <div style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1060,
                        width: '90%',
                        maxWidth: '500px'
                    }}>
                        <div className="card shadow-lg"
                            style={{
                                borderRadius: '16px',
                                border: '2px solid #FFD700'
                            }}>
                            {/* Header */}
                            <div className="card-header d-flex justify-content-between align-items-center"
                                style={{
                                    background: 'linear-gradient(135deg, #8B0000 0%, #B22222 100%)',
                                    borderRadius: '14px 14px 0 0',
                                    borderBottom: '2px solid #FFD700'
                                }}>
                                <h5 className="mb-0 fw-bold"
                                    style={{ color: '#FFD700' }}>
                                    Cancel Booking #{selectedBooking}
                                </h5>
                                <button
                                    type="button"
                                    onClick={closeCancelModal}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#FFD700',
                                        fontSize: '24px',
                                        lineHeight: 1,
                                        cursor: 'pointer'
                                    }}>
                                    ×
                                </button>
                            </div>

                            {/* Body */}
                            <div className="card-body p-4">
                                <label className="form-label fw-bold"
                                    style={{ color: '#8B0000' }}>
                                    Cancellation Reason
                                </label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    autoFocus
                                    placeholder="Enter reason for cancellation"
                                    value={cancelReason}
                                    onChange={(e) =>
                                        setCancelReason(e.target.value)}
                                    style={{ borderColor: '#FFD700' }}>
                                </textarea>
                            </div>

                            {/* Footer */}
                            <div className="card-footer d-flex justify-content-end gap-2"
                                style={{
                                    backgroundColor: '#FFF5F5',
                                    borderRadius: '0 0 14px 14px'
                                }}>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={closeCancelModal}
                                    disabled={cancelling}>
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger fw-bold"
                                    onClick={handleCancel}
                                    disabled={cancelling}>
                                    {cancelling
                                        ? 'Cancelling...'
                                        : 'Confirm Cancel'}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default MyBookings;